import { useCallback, useEffect, useRef, useState } from 'react';

import { useAdvertiser } from '../../AdvertiserContext';
import { APIAdsAccount, APIAdsDictionaries, APIAdsPlacements } from '../../api_ads';

/**
 * Справочники и рекламный аккаунт.
 *
 * Кэш держим на уровне модуля: справочники грузятся из БД (города, хобби,
 * параметры тестов) и весят прилично, а открывать список кампаний партнёр
 * будет десятки раз за сессию. Перезагружать их каждый раз бессмысленно.
 */
let _cache = { placements: null, dictionaries: null };

export const resetAdsCache = () => { _cache = { placements: null, dictionaries: null }; };


export function useAdsAccount() {
    const { advertiserToken: token } = useAdvertiser();
    const [account, setAccount] = useState(null);
    const [limits, setLimits] = useState(null);
    const [geoPricing, setGeoPricing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsAccount(token);
            setAccount(res?.account || null);
            setLimits(res?.limits || null);
            setGeoPricing(res?.geo_pricing || []);
        } catch (e) {
            // 503 — модуль выключен глобально администратором
            setError(e?.response?.status === 503
                ? 'Рекламный модуль временно отключён'
                : 'Не удалось загрузить рекламный аккаунт');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    return { account, limits, geoPricing, loading, error, reload: load };
}


export function useAdsPlacements() {
    const { advertiserToken: token } = useAdvertiser();
    const [placements, setPlacements] = useState(_cache.placements || []);
    const [loading, setLoading] = useState(!_cache.placements);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        if (_cache.placements) { setLoading(false); return; }
        if (!token) return;

        (async () => {
            try {
                const res = await APIAdsPlacements(token);
                _cache.placements = res?.items || [];
                if (mounted.current) setPlacements(_cache.placements);
            } catch (e) {
                if (mounted.current) setPlacements([]);
            } finally {
                if (mounted.current) setLoading(false);
            }
        })();

        return () => { mounted.current = false; };
    }, [token]);

    return { placements, loading };
}


export function useAdsDictionaries() {
    const { advertiserToken: token } = useAdvertiser();
    const [dictionaries, setDictionaries] = useState(_cache.dictionaries || null);
    const [loading, setLoading] = useState(!_cache.dictionaries);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        if (_cache.dictionaries) { setLoading(false); return; }
        if (!token) return;

        (async () => {
            try {
                const res = await APIAdsDictionaries(token);
                _cache.dictionaries = res || null;
                if (mounted.current) setDictionaries(_cache.dictionaries);
            } catch (e) {
                if (mounted.current) setDictionaries(null);
            } finally {
                if (mounted.current) setLoading(false);
            }
        })();

        return () => { mounted.current = false; };
    }, [token]);

    return { dictionaries, loading };
}